/* Import components */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { DataTablePaginated } from '../components';
import { axios } from '../libs/axios';
import { useSelector } from 'react-redux';
import { User } from '../states/actions';
import ReactPaginate from 'react-paginate';
import { useTranslation } from '../i18n/I18nContext';
import logger from '../utils/logger';
import {
	capitalizeName,
	validateCompanyRegCode,
	validateEmail,
	validateName,
	validatePID,
	validatePhone,
} from '../utils/clientValidation';

/* Format date */

function formatDateDMY(date) {
	if (!date) return '';
	const d = new Date(date);
	const day = d.getDate().toString().padStart(2, '0');
	const month = (d.getMonth() + 1).toString().padStart(2, '0');
	const year = d.getFullYear();
	return `${day}.${month}.${year}`;
}

/* Client type filter */

const ClientTypeFilter = ({ typeFilter, onChange, t }) => {
	const opts = [
		{ label: t('clients.all'),      value:'all',     },
		{ label: t('clients.persons'),  value:'person', },
		{ label: t('clients.businesses'), value:'business',  },
	];
	return (
		<>
			{opts.map(o => {
				const sel = typeFilter === o.value;
				return (
					<div
						key={o.value}
						onClick={() => onChange(o.value)}
						className={`status status-${o.value}${sel ? ' selected' : ''}`}>
						{o.value !== 'all' && (<span className="indicator"/>)}
						{o.label}
					</div>
				);
			})}
		</>
	);
};

// Custom submit button component that can access form values and perform validation
const CustomSubmitButton = ({ isEditing, closeModal, editingId, setEmailError, setPhoneError, setPidError, setNameError, setCompanyRegError, setGeneralError, form, setForm, onSubmit, user, showClientExistsDialog, existingClientEmail, onAssignToClient, onCancelAssignment, t }) => {
	const handleSubmit = async (e) => {
		e.preventDefault();

		// Clear any previous validation errors
		setEmailError('');
		setPhoneError('');
		setPidError('');
		setNameError('');
		setCompanyRegError('');
		setGeneralError('');

		// Auto-trim firstname and lastname before validation
		const trimmedForm = {
			...form,
			firstname: form.firstname ? form.firstname.trim() : '',
			lastname: form.lastname ? form.lastname.trim() : ''
		};
		
		// Update form state with trimmed values
		if (setForm) {
			setForm(trimmedForm);
		}

		// Client-side validation first (for all users)
		let hasValidationErrors = false;

		// Validate first name (required)
		const firstNameValidation = validateName(trimmedForm.firstname, t);
		if (!firstNameValidation.isValid) {
			setNameError(firstNameValidation.error);
			hasValidationErrors = true;
		}

		// Validate last name (required)
		const lastNameValidation = validateName(trimmedForm.lastname, t);
		if (!lastNameValidation.isValid) {
			setNameError(lastNameValidation.error);
			hasValidationErrors = true;
		}

		// Validate email format (required)
		const emailValidation = validateEmail(trimmedForm.email, t);
		if (!emailValidation.isValid) {
			setEmailError(emailValidation.error);
			hasValidationErrors = true;
		}

		// Validate phone format (required)
		const phoneValidation = validatePhone(trimmedForm.phone, t);
		if (!phoneValidation.isValid) {
			setPhoneError(phoneValidation.error);
			hasValidationErrors = true;
		}

		// Validate PID format (required)
		const pidValidation = validatePID(trimmedForm.pid, t);
		if (!pidValidation.isValid) {
			setPidError(pidValidation.error);
			hasValidationErrors = true;
		}

		// Validate company registration code (only for business clients, optional)
		if (trimmedForm.type === 'business' && trimmedForm.business_reg_code) {
			const regCodeValidation = validateCompanyRegCode(trimmedForm.business_reg_code, t);
			if (!regCodeValidation.isValid) {
				setCompanyRegError(regCodeValidation.error);
				hasValidationErrors = true;
			}
		}

		// If there are client-side validation errors, stop here
		if (hasValidationErrors) {
			return;
		}

		// For lawyer users: skip backend validation and let backend handle it
		if (user && user.role === 'lawyer') {
			onSubmit(e);
			return;
		}

		// For admin users: do backend validation as before

		// Validate email before submission
		if (trimmedForm.email && trimmedForm.email.trim()) {
			try {
				const data = await axios.get(`/clients/check-email?email=${encodeURIComponent(trimmedForm.email)}${editingId ? `&excludeId=${editingId}` : ''}`);

				if (data && data.exists) {
					setEmailError(t('clients.emailInUse'));
					return; // Stop submission
				}
			} catch (error) {
				logger.error('Email validation error:', error);
				setEmailError(t('clients.emailValidateError'));
				return; // Stop submission
			}
		}

		// Validate phone before submission
		if (trimmedForm.phone && trimmedForm.phone.trim()) {
			try {
				const data = await axios.get(`/clients/check-phone?phone=${encodeURIComponent(trimmedForm.phone)}${editingId ? `&excludeId=${editingId}` : ''}`);

				if (data && data.exists) {
					setPhoneError(t('clients.phoneInUse'));
					return; // Stop submission
				}
			} catch (error) {
				logger.error('Phone validation error:', error);
				setPhoneError(t('clients.phoneValidateError'));
				return; // Stop submission
			}
		}

		// Validate PID before submission
		if (trimmedForm.pid && trimmedForm.pid.trim()) {
			try {
				const data = await axios.get(`/clients/check-pid?pid=${encodeURIComponent(trimmedForm.pid)}${editingId ? `&excludeId=${editingId}` : ''}`);

				if (data && data.exists) {
					setPidError(t('clients.pidInUse'));
					return; // Stop submission
				}
			} catch (error) {
				logger.error('PID validation error:', error);
				setPidError(t('clients.pidValidateError'));
				return; // Stop submission
			}
		}

		// If all validation passes, submit the form
		onSubmit(e);
	};

	// If showing client exists dialog, show assignment confirmation instead of normal buttons
	if (showClientExistsDialog) {
		return (
			<div className="form-row col-full row-actions">
				<div style={{ marginBottom: '20px', textAlign: 'center' }}>
					<p style={{ fontSize: '16px', marginBottom: '20px', color: '#333' }}>
						{t('clients.existsText', { email: existingClientEmail })}
					</p>
				</div>
				<div className="form-row col-full row-actions">
					<button 
						type="button" 
						className="button button-large" 
						onClick={onCancelAssignment}
					>
						{t('common.no')}
					</button>
					<button
						type="button"
						className="button button-large button-blue"
						onClick={onAssignToClient}
					>
						{t('common.yes')}
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="form-row col-full row-actions">
			<button type="button" className="button button-large" onClick={closeModal}>{t('common.cancel')}</button>
			<button
				type="button"
				className="button button-large button-blue"
				onClick={handleSubmit}
			>
				{isEditing ? t('common.update') : t('common.create')}
			</button>
		</div>
	);
};

/* Partner filter for admin users */

const PartnerFilter = ({ partnerFilter, onChange, crmUsers, loading, t }) => {
	return (
		<select 
			value={partnerFilter} 
			onChange={(e) => onChange(e.target.value)}
			disabled={loading}
		>
			<option value="">{t('common.allPartners')}</option>
			{crmUsers
				.filter(user => user.role === 'lawyer')
				.map((user) => (
					<option key={user._id} value={user._id}>
						{user.firstName} {user.lastName} {user.partner_company_name ? `(${user.partner_company_name})` : ''}
					</option>
				))}
		</select>
	);
};

// Export clients */

export const Clients = () => {

	/* Get current user for role-based permissions */
	const user = useSelector(User.get());
	const { t } = useTranslation();

	/* Table / paging state */
	
	const [rawClients,   setRawClients]   = useState([]);
	const [clients,      setClients]      = useState([]);
	const [loading,      setLoading]      = useState(false);
	const [currentPage,  setCurrentPage]  = useState(1);
	
	// Simple wrapper for setCurrentPage
	const setCurrentPageDebug = (newPage) => {
		setCurrentPage(newPage);
	};
	const [totalRecords, setTotalRecords] = useState(0);
	const [sizePerPage,  setSizePerPage]  = useState(100);
	const [search,       setSearch]       = useState('');
	const [typeFilter,   setTypeFilter]   = useState('all'); // 'all', 'person', 'business'
	const [partnerFilter, setPartnerFilter] = useState(''); // Partner filter for admin users
	const [sortField,    setSortField]    = useState('createdAt');
	const [sortOrder,    setSortOrder]    = useState('desc');
	
	/* Modal state */
	
	const blank = { 
		firstname:'', 
		lastname:'', 
		email:'', 
		phone:'', 
		pid:'', 
		related_lawyers:[],
		type: 'person',
		business_name: '',
		business_address: '',
		business_reg_code: ''
	};
	
	const [showModal, setShowModal] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [editId,    setEditId]    = useState(null);
	const [form,      setForm]      = useState(blank);
	
	/* Validation error states */
	const [emailError, setEmailError] = useState('');
	const [phoneError, setPhoneError] = useState('');
	const [pidError, setPidError] = useState('');
	const [nameError, setNameError] = useState('');
	const [companyRegError, setCompanyRegError] = useState('');
	const [generalError, setGeneralError] = useState('');
	
	/* Client exists confirmation state */
	const [showClientExistsDialog, setShowClientExistsDialog] = useState(false);
	const [existingClientId, setExistingClientId] = useState(null);
	const [existingClientEmail, setExistingClientEmail] = useState('');
	
	/* Lawyer assignment state - using same pattern as Projects page */
	const [crmUsers, setCrmUsers] = useState([]);
	const [selectedLawyers, setSelectedLawyers] = useState([]);
	const [lawyerLoading, setLawyerLoading] = useState(false);
	
	/* Fetch CRM users for lawyer assignment - exact same as Projects page */
	const fetchCrmUsers = async() => {
		if (user && user.role === 'admin') {
			try {
				setLawyerLoading(true);
				const users = await axios.get('/projects/crm-users/all');
				setCrmUsers(users);
			} catch (error) {
				logger.error('Error fetching CRM users:', error);
			} finally {
				setLawyerLoading(false);
			}
		}
	};

	/* Fetch clients */
	
	const fetchClients = async(page=1, size=100, q='', qtype='clients', type='all', partner='', sortField='createdAt', sortOrder='desc') => {
		setLoading(true);
		try {
			const response = await axios.get('/clients',
				{
					params: {
						page,
						sizePerPage: size,
						q,
						qtype,
						type,
						partner,
						sortField,
						sortOrder,
					}
				});
			const { clients: rows, total } = response;
			setRawClients(rows || []);
			setTotalRecords(total || 0);
			setClients(rows || []);
		} catch (error) {
			logger.error('Error fetching clients:', error);
		} finally {
			setLoading(false);
		}
	};
	
	useEffect(()=>{ 
		fetchClients(currentPage, sizePerPage, search, 'clients', typeFilter, partnerFilter, sortField, sortOrder);
		fetchCrmUsers();
	}, [currentPage, sizePerPage, search, typeFilter, partnerFilter, sortField, sortOrder]);

	// Handle URL parameters for client modal
	useEffect(() => {
		const pathParts = window.location.pathname.split('/');
		const clientId = pathParts[pathParts.length - 1];
		
		// Check if we're on a client detail URL (/client/:id)
		if (window.location.pathname.startsWith('/client/') && clientId && clientId !== 'clients') {
			if (clients.length > 0) {
				// Find client by ID in the current clients list
				const client = clients.find(c => c._id === clientId);
				if (client) {
					openEdit(client);
				} else {
					// If client not found in current list, fetch it directly
					fetchClientById(clientId);
				}
			}
		}
	}, [clients]);

	// Handle browser back/forward navigation
	useEffect(() => {
		const handlePopState = () => {
			const pathParts = window.location.pathname.split('/');
			const clientId = pathParts[pathParts.length - 1];
			
			if (window.location.pathname.startsWith('/client/') && clientId && clientId !== 'clients') {
				// URL has client ID, open modal
				if (clients.length > 0) {
					const client = clients.find(c => c._id === clientId);
					if (client) {
						openEdit(client);
					} else {
						fetchClientById(clientId);
					}
				}
			} else {
				// Not on client detail URL, close modal if open
				if (showModal) {
					closeModal();
				}
			}
		};

		window.addEventListener('popstate', handlePopState);
		return () => window.removeEventListener('popstate', handlePopState);
	}, [clients, showModal]);

	// Fetch client by ID for URL access
	const fetchClientById = async (clientId) => {
		try {
			const client = await axios.get(`/clients/${clientId}`);
			openEdit(client);
		} catch (error) {
			logger.error('Error fetching client by ID:', error);
			window.nError({
				title: t('common.errorTitle'), 
				text: t('clients.notFound'), 
				icon: 'fa-exclamation-triangle'
			});
		}
	};

	
	/* Local search fallback - DISABLED since we're using remote search */
	
	// useEffect(() => {
	// 	const s=search.trim().toLowerCase();
	// 	setClients(s ? rawClients.filter(c => {
	// 		return `${c.firstname} ${c.lastname}`.toLowerCase().includes(s) ||
	// 		c.email.toLowerCase().includes(s) ||
	// 		c.phone.includes(s) || (c.pid||'').includes(s);
	// 	}): rawClients);
	// },[search,rawClients]);
	
	/* Handle table sorting */
	
	const handleTableChange = (type, { sortField, sortOrder }) => {
		if (type === 'sort') {
			setSortField(sortField);
			setSortOrder(sortOrder);
			setCurrentPageDebug(1); // Reset to first page when sorting
		}
	};

	
	/* Lawyer assignment handlers - using same pattern as Projects page */
	const addLawyer = (userNo) => {
		const selectedUser = crmUsers.find(u => u.user_no === userNo && u.role === 'lawyer');
		if (selectedUser) {
			const lawyerData = {
				_id: selectedUser._id,
				name: `${selectedUser.firstName} ${selectedUser.lastName}`,
				email: selectedUser.email
			};
			
			const isAlreadySelected = selectedLawyers.some(l => l._id === selectedUser._id);
			if (!isAlreadySelected) {
				const newSelectedLawyers = [...selectedLawyers, lawyerData];
				setSelectedLawyers(newSelectedLawyers);
				// Update form data
				setForm(prev => ({
					...prev,
					related_lawyers: newSelectedLawyers,
					related_partner_ids: newSelectedLawyers.map(l => l._id)
				}));
			}
		}
	};

	const removeLawyer = (lawyerId) => {
		const newSelectedLawyers = selectedLawyers.filter(l => l._id !== lawyerId);
		setSelectedLawyers(newSelectedLawyers);
		// Update form data
		setForm(prev => ({
			...prev,
			related_lawyers: newSelectedLawyers,
			related_partner_ids: newSelectedLawyers.map(l => l._id)
		}));
	};

	/* Input validation and formatting handlers */
	
	const handleInputChange = (fieldName, value) => {
		let processedValue = value;
		
		// Auto-capitalize names
		if (fieldName === 'firstname' || fieldName === 'lastname') {
			processedValue = capitalizeName(value);
		}
		
		// Update form state
		setForm({ ...form, [fieldName]: processedValue });
	};
	
	// Handle blur event to auto-trim leading/trailing spaces for name fields
	const handleInputBlur = (fieldName, value) => {
		if (fieldName === 'firstname' || fieldName === 'lastname') {
			const trimmedValue = value.trim();
			if (trimmedValue !== value) {
				// Only update if value changed (had leading/trailing spaces)
				setForm({ ...form, [fieldName]: trimmedValue });
			}
		}
	};

	/* CRUD helpers */
	
	const openNew  = () => { 
		setForm(blank); 
		setIsEditing(false); 
		setShowModal(true); 
		setSelectedLawyers([]);
		setEmailError(''); 
		setPhoneError(''); 
		setPidError(''); 
		setNameError('');
		setCompanyRegError('');
		setGeneralError('');
		
		// Navigate to clients list when opening new client
		window.history.pushState({}, '', '/clients');
	};

	const closeModal = () => {
		setShowModal(false);
		setForm(blank);
		setSelectedLawyers([]);
		setEmailError('');
		setPhoneError('');
		setPidError('');
		setNameError('');
		setCompanyRegError('');
		setGeneralError('');
		
		// Navigate back to clients list when closing modal
		window.history.pushState({}, '', '/clients');
	};
	const openEdit = cl => { 
		setForm({
			...cl, 
			related_lawyers: cl.related_lawyers || []
		}); 
		setSelectedLawyers(cl.related_lawyers || []);
		setIsEditing(true); 
		setEditId(cl._id); 
		setShowModal(true); 
		setEmailError(''); 
		setPhoneError(''); 
		setPidError(''); 
		setNameError('');
		setCompanyRegError('');
		setGeneralError('');
		
		// Update URL to client detail page
		window.history.pushState({}, '', `/client/${cl._id}`);
	};
	
	const handleCreate = async e => {
		e.preventDefault();
		
		// Clear any previous general errors
		setGeneralError('');
		
		// Auto-trim firstname and lastname before submission
		const trimmedForm = {
			...form,
			firstname: form.firstname ? form.firstname.trim() : '',
			lastname: form.lastname ? form.lastname.trim() : ''
		};
		
		// Update form state with trimmed values
		setForm(trimmedForm);
		
		const result = await axios.post('/clients', trimmedForm);
		
		// Check if the result is an error response (axios wrapper returns errors instead of throwing them)
		if (result && result.status === 409) {
			// Handle special case for lawyer users when client already exists
			if (result.clientExists && user.role === 'lawyer') {
				setExistingClientId(result.existingClientId);
				setExistingClientEmail(form.email);
				setShowClientExistsDialog(true);
			} else if (result.validationErrors && user.role === 'lawyer') {
				// Handle validation errors for lawyer users (phone/PID duplicates)
				if (result.validationErrors.phone) {
					setPhoneError(result.validationErrors.phone);
				}
				if (result.validationErrors.pid) {
					setPidError(result.validationErrors.pid);
				}
			} else {
				// Handle other 409 errors
				setGeneralError(t('clients.createError', { message: result.error || t('common.unknownError') }));
			}
		} else if (result && result.status && result.status >= 400) {
			// Handle other error statuses
			setGeneralError(t('clients.createError', { message: result.error || result.message || t('common.unknownError') }));
		} else {
			// Success - close modal and refresh
			closeModal();
			fetchClients(currentPage, sizePerPage, search, 'clients', typeFilter, partnerFilter, sortField, sortOrder);
		}
	};
	
	const handleUpdate = async e => {
		e.preventDefault();
		
		// Auto-trim firstname and lastname before submission
		const trimmedForm = {
			...form,
			firstname: form.firstname ? form.firstname.trim() : '',
			lastname: form.lastname ? form.lastname.trim() : ''
		};
		
		// Update form state with trimmed values
		setForm(trimmedForm);
		
		await axios.put(`/clients/${editId}`, trimmedForm);
		closeModal();
		fetchClients(currentPage, sizePerPage, search, 'clients', typeFilter, partnerFilter);
	};
	
	// Handle assignment to existing client
	const handleAssignToExistingClient = async () => {
		try {
			await axios.post(`/clients/${existingClientId}/assign-lawyer`);
			setShowClientExistsDialog(false);
			setExistingClientId(null);
			setExistingClientEmail('');
			setShowModal(false);
			// Reset form to blank state
			setForm(blank);
			setSelectedLawyers([]);
			fetchClients(currentPage, sizePerPage, search, 'clients', typeFilter, partnerFilter, sortField, sortOrder);
			window.nSuccess({title: t('common.successTitle'), text: t('clients.assignSuccess'), icon: 'fa-check-square'});
		} catch (error) {
			logger.error('Error assigning to client:', error);
			window.nError({title: t('common.errorTitle'), text: t('clients.assignError', { message: error.response?.data?.error || error.message }), icon: 'fa-exclamation-triangle'});
		}
	};
	
	// Handle cancel assignment
	const handleCancelAssignment = () => {
		setShowClientExistsDialog(false);
		setExistingClientId(null);
		setExistingClientEmail('');
		// Reset form to blank state
		setForm(blank);
		setSelectedLawyers([]);
		setEmailError('');
		setPhoneError('');
		setPidError('');
		setNameError('');
		setCompanyRegError('');
		setGeneralError('');
	};
	
	/* DELETE with window.nConfirm  */
	
	const handleDelete = (id, name) => {
		let ntfy; // will hold the Noty instance
		ntfy = window.nConfirm({
			icon  : 'fa-question-circle',
			title : t('clients.deleteConfirm'),
			text  : `<span>${name}</span>`,
			
			/* YES → delete and close */
			
			onYes : async () => {
				try {
					await axios.delete(`/clients/${id}`);
					fetchClients(currentPage, sizePerPage, search, 'clients', typeFilter, partnerFilter, sortField, sortOrder);
				} catch (err) {
					logger.error('Error deleting client:', err);
				} finally {
					if (ntfy?.close) ntfy.close();
				}
			},
			
			/* NO → simply close */
			
			onNo : () => {
				if (ntfy?.close) ntfy.close();
			}
		});
	};
	
	/* Format phone numbers to Estonian format +372 XXXXXXXX */
	
	const formatPhoneNumber = (phone) => {
		if (!phone) return '';
		
		/* Remove all spaces */
		
		let cleaned = phone.replace(/\s+/g, '');
		
		/* Remove leading 372 if it doesn't have + */
		
		if (cleaned.startsWith('372')) {
			cleaned = '+' + cleaned;
		}
		
		/* If the number already starts with +372, keep it */
		
		if (cleaned.startsWith('+372')) {
			return `${cleaned.slice(0, 4)} ${cleaned.slice(4)}`; // Format as +372 XXXXXXXX
		}
		
		/* If it doesn't match the Estonian format, return as-is */
		
		return phone;
	};
	
	/* Table columns */
	
	const tableColumns = useMemo(() => {
		if (typeFilter === 'business') {
			// Business clients layout: Business Name first, then Representative, no subscription
			return [
				{
					dataField: 'business_name',
					text: t('clients.businessName'),
					classes: 'cell-business-name',
					headerClasses: 'cell-business-name',
					formatter: (businessName, r) => {
						const name = businessName || '-';
						const isSupport = user && user.role === 'support';
						return (
							<div 
								className={`inner ${!isSupport ? 'clickable-name' : ''} business-name-cell`}
								onClick={!isSupport ? () => openEdit(r) : undefined}
								style={{textAlign: 'left', wordBreak: 'break-word', whiteSpace: 'pre-wrap'}}
							>
								{name}
							</div>
						);
					},
				},
				{
					dataField: 'name',
					text: t('clients.representative'),
					classes: 'cell-name',
					headerClasses: 'cell-name',
					formatter: (_, r) => {
						const fullName = `${r.firstname ?? ''} ${r.lastname ?? ''}`.trim();
						const isSupport = user && user.role === 'support';
						return (
							<div
								className={`inner ${!isSupport ? 'clickable-name' : ''}`}
								onClick={!isSupport ? () => openEdit(r) : undefined}
								style={{textAlign: 'left'}}
							>
								{fullName}
							</div>
						);
					},
				},
				{
					dataField: 'business_reg_code',
					text: t('clients.registrationCode'),
					classes: 'cell-business-reg',
					headerClasses: 'cell-business-reg',
					formatter: (businessRegCode, r) => {
						return (
							<div style={{textAlign: 'left'}}>
								{businessRegCode || '-'}
							</div>
						);
					},
				},
				{
					dataField: 'email',
					text: t('clients.email'),
					classes: 'cell-email',
					headerClasses: 'cell-email',
					formatter: (email, r) => <a href={`mailto:${email}`} title={t('common.sendEmail')}>{email}</a>,
				},
				{
					dataField: 'phone',
					text: t('clients.phone'),
					classes: 'cell-phone',
					headerClasses: 'cell-phone',
				},
				{
					dataField: 'createdAt',
					text: t('clients.created'),
					classes: 'cell-created',
					headerClasses: 'cell-created',
					formatter: d => formatDateDMY(d),
				},
				// Only show assigned lawyers column for admin users
				...(user && user.role === 'admin' ? [{
					dataField: 'related_lawyers',
					text: t('clients.assignedLawyers'),
					classes: 'cell-lawyers',
					headerClasses: 'cell-lawyers',
					formatter: (lawyers, r) => {
						if (!lawyers || lawyers.length === 0) {
							return <span className="text-muted">{t('clients.noLawyersAssigned')}</span>;
						}
						return (
							<div className="assigned-lawyers">
								{lawyers.map((lawyer, index) => (
									<div key={lawyer._id || index} className="lawyer-item">
										<span className="lawyer-name">{lawyer.name}</span>
									</div>
								))}
							</div>
						);
					}
				}] : []),
				{
					dataField: 'actions',
					text: t('clients.actions'),
					classes: 'cell-actions',
					headerClasses: 'cell-actions',
					sort: false,
					formatter: (_,r) => {
						const isAdmin = user && user.role === 'admin';
						const isSupport = user && user.role === 'support';
						return (
							<div className='actions'>
								{!isSupport && (
									<button className="button button-small" onClick={ e => {
										e.stopPropagation();
										openEdit(r);
									}}>
										{t('common.edit')}
									</button>
								)}
								{/* Only show delete button if the user is an admin */}
								{isAdmin && (
									<button className="button button-small" onClick={ e => {
										e.stopPropagation();
										handleDelete(r._id, `${r.firstname} ${r.lastname}`);
									}}>
										{t('common.delete')}
									</button>
								)}
							</div>
						);
					}
				},
			];
		}

		// Default layout for persons and all clients
		const baseColumns = [
			{
				dataField: 'name',
				text: t('clients.client'),
				classes: 'cell-name',
				headerClasses: 'cell-name',
				formatter: (_, r) => {
					const fullName = `${r.firstname ?? ''} ${r.lastname ?? ''}`.trim();
					const isBusiness = r.type === 'business';
					const displayName = isBusiness ? r.business_name : fullName;
					const isSupport = user && user.role === 'support';
					
					return (
						<div
							className={`inner ${!isSupport ? 'clickable-name' : ''}`}
							onClick={!isSupport ? () => openEdit(r) : undefined}
						>
							<div className={`client-info ${isBusiness ? 'business' : 'person'}`}>
								<span className="client-name">{displayName}</span>
								{isBusiness && <span className="client-type-badge">{t('common.business')}</span>}
							</div>
						</div>
					);
				},
			},
			{
				dataField: 'email',
				text: t('clients.email'),
				classes: 'cell-email',
				headerClasses: 'cell-email',
				formatter: (email, r) => <a href={`mailto:${email}`} title={t('common.sendEmail')}>{email}</a>,
			},
			{
				dataField: 'phone',
				text: t('clients.phone'),
				classes: 'cell-phone',
				headerClasses: 'cell-phone',
			},
			{
				dataField: 'pid',
				text: t('clients.personalId'),
				classes: 'cell-id',
				headerClasses: 'cell-id',
			},
			{
				dataField: 'createdAt',
				text: 'Created',
				classes: 'cell-created',
				headerClasses: 'cell-created',
				formatter: d => formatDateDMY(d),
			},
			{
				dataField: 'activeSubscription',
				text: t('clients.subscription'),
				classes: 'cell-subscription',
				headerClasses: 'cell-subscription',
				formatter: (sub) => {
				if (!sub) return <span className="text-muted"></span>;
				return (
					<>
					<span title={t('clients.nextBilling', { date: formatDateDMY(sub.nextBillingDate) })}>
					{sub.plan}
					</span>
				{/* 	{sub.status}
					{`Next billing: ${formatDateDMY(sub.nextBillingDate)}`} */}
					</>
				);
				},
			},
			// Only show assigned lawyers column for admin users
			...(user && user.role === 'admin' ? [{
				dataField: 'related_lawyers',
				text: t('clients.assignedLawyers'),
				classes: 'cell-lawyers',
				headerClasses: 'cell-lawyers',
				formatter: (lawyers, r) => {
					if (!lawyers || lawyers.length === 0) {
						return <span className="text-muted">No lawyers assigned</span>;
					}
					return (
						<div className="assigned-lawyers">
							{lawyers.map((lawyer, index) => (
								<div key={lawyer._id || index} className="lawyer-item">
									<span className="lawyer-name">{lawyer.name}</span>
								</div>
							))}
						</div>
					);
				}
			}] : []),
			{
				dataField: 'actions',
				text: 'Actions',
				classes: 'cell-actions',
				headerClasses: 'cell-actions',
				sort: false,
				formatter: (_,r) => {
					const isAdmin = user && user.role === 'admin';
					const isSupport = user && user.role === 'support';
					return (
						<div className='actions'>
							{!isSupport && (
								<button className="button button-small" onClick={ e => {
									e.stopPropagation();
									openEdit(r);
								}}>
									Edit
								</button>
							)}
							{/* Only show delete button if the user is an admin */}
							{isAdmin && (
								<button className="button button-small" onClick={ e => {
									e.stopPropagation();
									handleDelete(r._id, `${r.firstname} ${r.lastname}`);
								}}>
									Delete
								</button>
							)}
						</div>
					);
				}
			},
		];

		return baseColumns;
	}, [user, typeFilter, t]);
	
	/* Form fields */

	const fields = [
		{ name: 'firstname', label: t('clients.fieldFirstName'), width: 'col-half' },
		{ name: 'lastname',  label: t('clients.fieldLastName'),  width: 'col-half' },
		{ name: 'email',     label: t('clients.fieldEmail'),     width: 'col-half' },
		{ name: 'phone',     label: t('clients.fieldPhone'),      width: 'col-half' },
		{ name: 'pid',       label: t('clients.fieldPersonalId'),width: 'col-full' },
		{ name: 'type',      label: t('clients.fieldClientType'), width: 'col-full', type: 'select', options: [
			{ value: 'person', label: t('clients.fieldPerson') },
			{ value: 'business', label: t('clients.fieldBusiness') }
		]},
		// Business fields - only show when type is business
		{ name: 'business_name', label: t('clients.fieldBusinessName'), width: 'col-full', showWhen: 'business' },
		{ name: 'business_address', label: t('clients.fieldBusinessAddress'), width: 'col-full', showWhen: 'business' },
		{ name: 'business_reg_code', label: t('clients.fieldRegistrationCode'), width: 'col-half', showWhen: 'business' },
		// Lawyer assignment - only show for admin users
		...(user && user.role === 'admin' ? [{ name: 'related_lawyers', label: t('clients.fieldAssignedLawyers'), width: 'col-full', type: 'lawyers' }] : [])
	];
	
	/* Render HTML */
	
	return (
		<div id="clients" className="row table_v2 clients">
			<div className="col p-0">
				<div className="x_panel chatbot-builder">
					<div className="header-menu d-flex justify-content-between align-items-center">
						<div className="header-title d-flex align-items-center">
							<h1>{t('clients.title')}</h1>
						</div>
						<div className="header-actions d-flex align-items-center">
							<div className="action action-search">
								<input
								type        = "text"
								placeholder = {t('clients.search')}
								value       = {search}
								onChange    = {e=>{ setSearch(e.target.value); setCurrentPageDebug(1); }}
								/>
								<i class="fa-solid fa-magnifying-glass"></i>
							</div>
							<div className="action action-addnew">
								<button className="button button-blue" onClick={openNew}>
									{t('common.addNew')}
								</button>
							</div>
						</div>
					</div>
					<div className="x_content">
						<div className="page-container d-flex flex-column">
							{loading ? <div>{t('common.loading')}</div> : (
							<>
								<div className="page-header d-flex justify-content-between align-items-center">
									<div className="filter-group d-flex align-items-center">
										<div className="filter filter-client-type">
											<ClientTypeFilter 
												typeFilter={typeFilter} 
												onChange={(type) => {
													setTypeFilter(type);
													setCurrentPageDebug(1);
												}}
												t={t}
											/>
										</div>
										{/* Partner filter - only show for admin users */}
										{user.role === 'admin' && (
											<div className="filter filter-partner" style={{ marginLeft: '20px' }}>
												<PartnerFilter 
													partnerFilter={partnerFilter}
													onChange={(partner) => {
														setPartnerFilter(partner);
														setCurrentPageDebug(1);
													}}
													crmUsers={crmUsers}
													loading={lawyerLoading}
													t={t}
												/>
											</div>
										)}
									</div>
									<div className='filter filter-pagesize d-flex justify-content-end align-items-center'>
										<label htmlFor="pageSize">{t('common.itemsPerPage')}</label>
									<select id="pageSize" value={sizePerPage} onChange={e=>{setSizePerPage(+e.target.value);setCurrentPage(1);}}>
										{[100,200,300,500].map(n=><option key={n} value={n}>{n}</option>)}
									</select>
									</div>
								</div>
								<div className="page-content nopaginate">
									<DataTablePaginated
									data          = {clients}
									columns       = {tableColumns}
									onTableChange = {handleTableChange} // ← kuulab sortimist
									rowClassName  = {()=>'table-row'}
									/>
								</div>
								<div className="page-footer d-flex justify-content-between align-items-center">
									<div className='filter filter-pagination d-flex align-items-center'>
										{Math.ceil(totalRecords / sizePerPage) > 0 && (
										<ReactPaginate
											containerClassName    = {'pagination'}
											activeClassName       = {'active'}
											previousLinkClassName = {'fa-regular fa-chevron-left'}
											nextLinkClassName     = {'fa-regular fa-chevron-right'}
											previousClassName     = {currentPage === 1 ? 'd-none' : 'previous'}
											nextClassName         = {currentPage === Math.ceil(totalRecords / sizePerPage) ? 'd-none' : 'next'}
											previousLabel         = {t('common.previous')}
											nextLabel             = {t('common.next')}
											breakLabel            = {t('common.ellipsis')}
											pageCount             = {Math.ceil(totalRecords/sizePerPage)}
											marginPagesDisplayed  = {2}
											pageRangeDisplayed    = {5}
											onPageChange          = {e=>{
												setCurrentPageDebug(e.selected+1);
											}}
											forcePage             = {currentPage-1}
										/>
										)}
									</div>
									<div className='filter filter-pagesize d-flex justify-content-end align-items-center'>
										<label htmlFor="pageSize">{t('common.itemsPerPage')}</label>
									<select id="pageSize" value={sizePerPage} onChange={e=>{setSizePerPage(+e.target.value);setCurrentPage(1);}}>
										{[100,200,300,500].map(n=><option key={n} value={n}>{n}</option>)}
									</select>
									</div>
								</div>
							</>
							)}
						</div>
					</div>
				</div>
			</div>
			{/* Modal */}
			{showModal && (
				<div className="modal client">
					<div className="modal-content">
						<div className="modal-header">
							<span className="modalclose" onClick={closeModal}>&times;</span>
							<h2>{isEditing ? t('clients.editTitle') : t('clients.newTitle')}</h2>
						</div>
						<div className="modal-form">
							<form onSubmit={isEditing ? handleUpdate : handleCreate}>
								{fields.map((f) => {
									// Skip business fields if client type is not business
									if (f.showWhen && form.type !== f.showWhen) {
										return null;
									}
									
									return (
										<div key={f.name} className={`form-row ${f.width} row-${f.name}`}>
											{f.type === 'lawyers' && user && user.role === 'admin' ? (
												<div className="lawyers-assignment">
													<label>{f.label}:</label>
													
													{/* Assigned lawyers list with remove buttons */}
													<div className="lawyers-list">
														{selectedLawyers.length > 0 ? (
															selectedLawyers.map((lawyer, index) => (
																<div key={lawyer._id || index} className="lawyer-item">
																	<span className="lawyer-name">{lawyer.name}</span>
																	<span className="lawyer-email">({lawyer.email})</span>
																	<button 
																		type="button" 
																		className="remove-lawyer-btn"
																		onClick={() => removeLawyer(lawyer._id)}
																		title={t('clients.removeLawyer')}
																	>
																		×
																	</button>
																</div>
															))
														) : (
															<span className="no-lawyers">{t('clients.noLawyersAssigned')}</span>
														)}
													</div>
													
													{/* Simple dropdown to add lawyers */}
													<div style={{marginTop: '10px'}}>
														<select
															value=""
															onChange={e => {
																if (e.target.value) {
																	addLawyer(e.target.value);
																	e.target.value = ""; // Reset dropdown
																}
															}}
															disabled={lawyerLoading}
														>
															<option value="">{t('clients.addLawyer')}</option>
															{crmUsers
																.filter(user => user.role === 'lawyer')
																.filter(lawyer => !selectedLawyers.some(selected => selected._id === lawyer._id))
																.map((user) => (
																<option key={user._id} value={user.user_no}>
																	{user.firstName} {user.lastName} ({user.role}) - {user.email}
																</option>
															))}
														</select>
														{lawyerLoading && <span style={{marginLeft: '10px', fontSize: '12px', color: '#666'}}>{t('common.loading')}</span>}
													</div>
												</div>
											) : f.type === 'select' ? (
												<>
													<label>{f.label}:</label>
													<select
														value={form[f.name]}
														onChange={e => setForm({ ...form, [f.name]: e.target.value })}
														required
													>
														{f.options.map(option => (
															<option key={option.value} value={option.value}>
																{option.label}
															</option>
														))}
													</select>
												</>
											) : (
												<>
													<input
														placeholder   = {f.label}
														required      = {['firstname', 'lastname', 'email'].includes(f.name)}
														type          = {f.name === 'email' ? 'email' : 'text'}
														value         = {form[f.name]}
														onChange      = {e => handleInputChange(f.name, e.target.value)}
														onBlur        = {e => handleInputBlur(f.name, e.target.value)}
														className    = {
															(f.name === 'email' && emailError) || 
															(f.name === 'phone' && phoneError) || 
															(f.name === 'pid' && pidError) ||
															((f.name === 'firstname' || f.name === 'lastname') && nameError) ||
															(f.name === 'business_reg_code' && companyRegError) ? 'error' : ''
														}
													/>
													{/* Display validation errors */}
													{f.name === 'email' && emailError && (
														<div className="email-error-message">
															{emailError}
														</div>
													)}
													{f.name === 'phone' && phoneError && (
														<div className="email-error-message">
															{phoneError}
														</div>
													)}
													{f.name === 'pid' && pidError && (
														<div className="email-error-message">
															{pidError}
														</div>
													)}
													{(f.name === 'firstname' || f.name === 'lastname') && nameError && (
														<div className="email-error-message">
															{nameError}
														</div>
													)}
													{f.name === 'business_reg_code' && companyRegError && (
														<div className="email-error-message">
															{companyRegError}
														</div>
													)}
												</>
											)}
										</div>
									);
								})}
								
								{/* General error message */}
								{generalError && (
									<div className="form-row col-full">
										<div className="email-error-message" style={{ textAlign: 'center', marginTop: '10px' }}>
											{generalError}
										</div>
									</div>
								)}
								
								<CustomSubmitButton
									isEditing={isEditing}
									closeModal={() => setShowModal(false)}
									editingId={editId}
									setEmailError={setEmailError}
									setPhoneError={setPhoneError}
									setPidError={setPidError}
									setNameError={setNameError}
									setCompanyRegError={setCompanyRegError}
									setGeneralError={setGeneralError}
									form={form}
									setForm={setForm}
									onSubmit={isEditing ? handleUpdate : handleCreate}
									user={user}
									showClientExistsDialog={showClientExistsDialog}
									existingClientEmail={existingClientEmail}
									onAssignToClient={handleAssignToExistingClient}
									onCancelAssignment={handleCancelAssignment}
									t={t}
								/>
							</form>
						</div>
					</div>
				</div>
			)}
			
			{/* Custom styles for lawyer assignment */}
			<style jsx>{`
				.lawyers-assignment {
					display: flex;
					flex-direction: column;
					gap: 10px;
				}
				
				.lawyers-list {
					display: flex;
					flex-direction: column;
					gap: 5px;
				}
				
				.lawyer-item {
					display: flex;
					align-items: center;
					padding: 5px 0;
					border-bottom: 1px solid #f0f0f0;
				}
				
				.lawyer-item:last-child {
					border-bottom: none;
				}
				
				.lawyer-name {
					font-weight: 500;
					margin-right: 8px;
				}
				
				.lawyer-email {
					color: #666;
					font-size: 14px;
					flex: 1;
				}
				
				.remove-lawyer-btn {
					background: none;
					border: none;
					color: #999;
					cursor: pointer;
					font-size: 16px;
					font-weight: bold;
					margin-left: auto;
					padding: 0;
					line-height: 1;
					display: flex;
					align-items: center;
					justify-content: center;
				}
				
				.remove-lawyer-btn:hover {
					color: #007bff;
				}
				
				.no-lawyers {
					color: #666;
					font-style: italic;
					font-size: 14px;
					padding: 10px 0;
				}
				
				/* Assigned lawyers column styling */
				.cell-lawyers {
					min-width: 150px;
					max-width: 200px;
				}
				
				.assigned-lawyers {
					display: flex;
					flex-direction: column;
					gap: 4px;
				}
				
				.lawyer-item {
					display: flex;
					align-items: center;
					padding: 2px 0;
					font-size: 13px;
					background: none !important;
					background-color: transparent !important;
					border: none !important;
					box-shadow: none !important;
					outline: none !important;
					text-decoration: none !important;
				}
				
				.lawyer-name {
					font-weight: 500;
					color: #032097;
					background: none !important;
					background-color: transparent !important;
					border: none;
					padding: 0;
					margin: 0;
				}
			`}</style>
		</div>
	);
};
